<template>
  <div
    ref="v-carousel"
    class="v-carousel"
    :style="smallMediumScrollStyle()"
    @mousedown.passive="(e) => scrollerTouchStart(e, true)"
    @mouseup.passive="(e) => scrollerTouchEnd(e, true)"
    @mousemove.passive="(e) => scrollerTouchMove(e, true)"
    @click="preventClickAfterDrag"
  >
    <div class="v-carousel-content">
      <div
        ref="v-carousel-wrapper"
        class="v-carousel-wrapper"
        @scroll="onScroll"
        @scrollend="onScrollEnd"
      >
        <div
          v-show="showArrowControl('prev')"
          class="v-button-wrapper prev"
          :class="{
            'hide-arrows':
              isTouchDevice && !keyboardUser && !isLargeTouchDevice,
          }"
        >
          <div
            ref="v-carousel-button-prev"
            class="v-carousel-button prev"
            :tabindex="manageArrowTabIndex('prev')"
            role="button"
            aria-label="Previous"
            @click="prev"
            @keyup.enter="prev"
          >
            <span class="icon chevron-left-arrow" />
          </div>
        </div>
        <div
          ref="v-scroller"
          class="v-scroller"
          @touchstart.passive="scrollerTouchStart"
          @touchmove.passive="scrollerTouchMove"
          @touchend.passive="scrollerTouchEnd"
        >
          <ul
            v-if="list.length"
            class="grid-x grid-margin-x v-carousel-container"
            aria-label="carousel"
          >
            <li
              v-for="(item, index) in itemList"
              v-show="lazyload ? index < maxItemsLazy : true"
              ref="carousel-slide"
              :key="index"
              :class="[`cell`, `${itemClass}`]"
              :style="slideStyle"
            >
              <slot
                name="item"
                :data="item"
                :index="index"
              />
            </li>

            <template
              v-for="(item, index) in numOfSkeletons"
              :key="index"
            >
              <li
                v-if="showSkeleton"
                :class="[
                  `cell`,
                  index === numOfSkeletons.length - 1 ? 'last' : '',
                ]"
              >
                <div
                  v-if="showSkeleton"
                  class="skeleton-class"
                >
                  <Skeleton :height="skeletonHeight" />
                </div>
              </li>
            </template>
          </ul>
        </div>
        <div
          v-show="showArrowControl('next')"
          class="v-button-wrapper next"
          :class="{
            'hide-arrows':
              isTouchDevice && !keyboardUser && !isLargeTouchDevice,
          }"
        >
          <div
            ref="v-carousel-button-next"
            class="v-carousel-button next"
            :tabindex="manageArrowTabIndex('next')"
            role="button"
            aria-label="Next"
            @click="next"
            @keyup.enter="next"
          >
            <span class="icon chevron-right-arrow" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Skeleton from '../skeleton';

export default {
  name: 'Carousel',
  components: {
    Skeleton,
  },
  props: {
    list: {
      type: Array,
      default: () => [],
    },
    itemClass: {
      type: String,
      default: '',
    },
    numberOfItems: {
      type: Object,
      default: () => ({
        large: 3,
        medium: 2.5,
        small: 1.5,
      }),
    },
    showSkeleton: {
      type: Boolean,
      default: false,
    },
    skeletonHeight: {
      type: String,
      default: '1rem',
    },
    maxItemsLazy: {
      type: Number,
    },
    gutter: {
      type: Object,
      default: () => ({
        large: 24,
        medium: 16,
        small: 8,
      }),
    },
    ssrSize: {
      type: String,
      default: null, // default null for backward compatibility
      validator: (value) => ['large', 'medium', 'small'].includes(value),
    },
  },
  emits: ['carousel-next', 'carousel-prev'],
  data() {
    return {
      itemsPerPage: 3,
      numberOfPages: 1,
      scrollerWidth: null,
      wrapWidth: null,
      childWidth: null,
      currentPage: 1,
      leftControl: false,
      rightControl: false,
      itemsLength: 0,
      scrollerX: 0,
      dragging: false,
      hasBeenDragged: false,
      gridPadding: 20, // $grid-container-padding csg variable hardcoded for carousel spacing
      isTouchDevice: false,
      keyboardUser: false,
      ssrOnly: true,
      skipScrollEnd: false,
      currentScrollLeft: 0,
      slideStyle: null, // only use when ssrOnly is true to render different width with css calc
    };
  },
  computed: {
    lazyload() {
      return this.maxItemsLazy > 0;
    },
    itemList() {
      if (this.lazyload) {
        return this.list.slice(0, this.maxItemsLazy);
      }
      return this.list;
    },
    numOfSkeletons() {
      let numSkeletons = Math.ceil(
        this.itemsPerPage - (this.list.length % this.itemsPerPage),
      );
      if (this.lazyload && this.list.length >= this.maxItemsLazy) {
        return 0;
      }
      if (this.lazyload && !Number.isInteger(this.itemsPerPage)) {
        // remaining items
        const remItems = this.list.length
          - (this.currentPage - 1) * Math.floor(this.itemsPerPage);
        // skeletons reqd -
        numSkeletons = Math.ceil(this.itemsPerPage - remItems);
        if (numSkeletons > 0) {
          return numSkeletons;
        }
        return 0;
      }
      return numSkeletons;
    },
    isLargeTouchDevice() {
      return this.isTouchDevice && window.innerWidth > 1024;
    },
  },
  watch: {
    currentPage() {
      this.updateTabIndex();
    },
    itemsLength() {
      this.updateTabIndex();
    },
    gutter: {
      deep: true,
      handler() {
        this.doSizing();
      },
    },
  },
  created() {
    this.ssrSlideStyle();
  },
  mounted() {
    this.ssrOnly = false; // turn off css calc for client side (use existing static width calculation)
    document.addEventListener('mouseup', this.documentMouseUp);
    document.addEventListener('mousemove', this.documentMouseMove);
    this.eventHandler();
    this.checkTouchDevice();
    window.addEventListener('resize', this.checkTouchDevice);
    document.addEventListener('keydown', this.handleKeydown);
    if (typeof window.onscrollend === 'undefined') {
      // eslint-disable-next-line
      const { scrollend } = require('scrollyfills');
      this.$refs['v-carousel-wrapper'].addEventListener('scrollend', this.onScrollEnd);
    }
  },
  beforeUnmount() {
    this.ro.unobserve(this.$refs['v-carousel']);
    document.removeEventListener('mouseup', this.documentMouseUp);
    document.removeEventListener('mousemove', this.documentMouseMove);
    window.removeEventListener('resize', this.checkTouchDevice);
    document.removeEventListener('keydown', this.handleKeydown);
    this.$refs['v-carousel-wrapper'].removeEventListener('scrollend', this.onScrollEnd);
  },
  updated() {
    if (this.itemsLength !== this.slotsItems().length) {
      this.redraw();
    }
  },
  methods: {
    ssrSlideStyle() {
      const isSSR = typeof window === 'undefined';
      // only use this during SSR and consumer passed ssrSize explicitly
      if (isSSR && this.ssrSize != null) {
        // const size = this.ssrSize;
        const initialItems = this.numberOfItems[this.ssrSize];
        const initialMargin = this.gutter[this.ssrSize] / 2;
        const hasPeekItem = Number.isInteger(initialItems) ? 0 : 1;
        const numberOfGutters = Math.floor(initialItems) + hasPeekItem;
        const isLarge = this.ssrSize === 'large';

        // For SSR, use css calc 100% to initial child width.
        // For non-SSR, use calculated child width from doSizing() to
        // maintain the static width when window size is on small/medium
        const width = `calc((100% - ${
          isLarge ? 0 : this.gridPadding
        }px - 2 * ${numberOfGutters} * ${initialMargin}px) / ${initialItems})`;
        this.slideStyle = `width: ${width}; margin-left: ${initialMargin}px; margin-right: ${initialMargin}px`;
      }
    },
    checkTouchDevice() {
      this.isTouchDevice = 'ontouchstart' in window
        || navigator.maxTouchPoints > 0
        || navigator.msMaxTouchPoints > 0;
    },

    smallMediumScrollStyle() {
      if (this.scrollerWidth != null) {
        if (this.windowSize() === 'small' || this.windowSize() === 'medium') {
          if (
            Math.abs(this.scrollerX) === 0
            || Math.abs(this.scrollerX) < this.gridPadding
          ) {
            // at the start itself, at page load or very little scroll to begin with, extend carousel window to the right
            return `margin-right:-${this.gridPadding}px;`;
          }
          if (
            // after user starts swiping much and in between pages, complete carousel window on the row, negative margins on both ends
            Math.abs(this.scrollerX) >= this.gridPadding
            && Math.abs(this.scrollerX) < this.addMarginPos()
          ) {
            return `margin-right: -${this.gridPadding}px; margin-left: -${this.gridPadding}px;`;
          } // last page when scrollerX > addMarginPos value, where last page items start, extend window to left
          return `margin-left: -${this.gridPadding}px;`;
        }
      }
      return null;
    },
    addMarginPos() {
      // scroller width -( 1.5 children width + 20px for smooth transition when it hits just 20px before last page view)
      const scrollAddMargin = this.scrollerWidth - this.wrapWidth - this.gridPadding;
      return scrollAddMargin;
    },

    documentMouseUp(event) {
      this.scrollerTouchEnd(event, true);
    },
    documentMouseMove(event) {
      this.scrollerTouchMove(event, true);
    },
    scrollerTouchStart(event, isMouse) {
      if (!this.leftControl && !this.rightControl) {
        return;
      }
      this.dragging = true;
      this.startX = this.scrollerX; // preserve current scroller position
      this.touchstartX = isMouse
        ? event.screenX
        : event.changedTouches[0].screenX;
    },
    onScroll(event) {
      if (this.skipScrollEnd) {
        return;
      }
      const padding = this.gridPadding + 1;
      const startedScrolling = event.target.scrollLeft >= this.gridPadding;
      const finishedScrolling = -(event.target.scrollLeft + this.gridPadding + 1);

      if (this.lazyload) {
        if (startedScrolling && event.target.scrollLeft + padding > this.addMarginPos()) {
          this.scrollerX = finishedScrolling;
          this.next(this.currentPage);
          return;
        }
      } else if (startedScrolling && event.target.scrollLeft < this.addMarginPos()) {
        this.scrollerX = finishedScrolling;
        return;
      }
      this.scrollerX = -event.target.scrollLeft;
    },
    onScrollEnd(event) {
      if (this.dragging || this.skipScrollEnd) {
        this.skipScrollEnd = false;
        return;
      }

      const dis = event.target.scrollLeft - this.currentScrollLeft;

      if (Math.abs(this.scrollerX) === 0 || Math.abs(this.scrollerX) < this.gridPadding) {
      // first page
        this.currentPage = 1;
        this.buttonHandler();
      } else if (Math.abs(this.scrollerX) >= this.addMarginPos()) {
      // last page
        this.currentPage = this.numberOfPages;
        this.buttonHandler();
      } else {
      // middle
        this.handleSwipe(dis);
      }

      this.currentScrollLeft = event.target.scrollLeft;
      this.skipScrollEnd = false;
    },
    updateNativeScroll(scrollerX, behavior) {
      this.$refs['v-carousel-wrapper'].scrollTo({
        top: 0,
        left: Math.abs(scrollerX),
        behavior,
      });
    },
    scrollerTouchMove(event, isMouse) {
      if (!this.dragging) {
        return;
      }
      this.hasBeenDragged = true;
      this.touchmoveX = isMouse
        ? event.screenX
        : event.changedTouches[0].screenX;
      const distance = this.touchstartX - this.touchmoveX;
      const nextX = this.startX - distance;
      if (nextX > 0) {
        this.scrollerX = 0;
        this.updateNativeScroll(this.scrollerX, 'auto');
        return;
      }
      if (nextX < this.wrapWidth - this.scrollerWidth + this.gridPadding) {
        this.scrollerX = this.wrapWidth - this.scrollerWidth;
        this.updateNativeScroll(this.scrollerX, 'auto');
        return;
      }
      this.scrollerX = nextX;
      this.updateNativeScroll(this.scrollerX, 'auto');
    },
    scrollerTouchEnd(event, isMouse) {
      if (!this.dragging) {
        return;
      }
      this.touchendX = isMouse
        ? event.screenX
        : event.changedTouches[0].screenX;
      const distance = this.touchstartX - this.touchendX;
      this.handleSwipe(distance);
      this.currentScrollLeft = this.$refs['v-carousel-wrapper'].scrollLeft;
      this.dragging = false;
    },
    preventClickAfterDrag() {
      if (this.hasBeenDragged) {
        this.hasBeenDragged = false;
      }
    },
    handleSwipe(distance) {
      const scrolledPages = Math.abs(
        distance
            / ((this.childWidth + 2 * this.marginSize())
              * Math.floor(this.itemsPerPage)),
      );
      if (distance > 0 && this.currentPage < this.numberOfPages) {
        const page = this.currentPage + scrolledPages;
        if (page > this.numberOfPages) {
          this.currentPage = this.numberOfPages;
        } else {
          this.currentPage += scrolledPages;
        }
        if (this.lazyload) {
          this.$emit(
            'carousel-next',
            this.currentPage,
            this.numberOfPages,
            false,
          );
          return;
        }
        this.$emit('carousel-next', this.currentPage);
      } else if (distance < 0 && this.currentPage > 1) {
        const page = this.currentPage - scrolledPages;
        if (page < 1) {
          this.currentPage = 1;
        } else {
          this.currentPage -= scrolledPages;
        }
        this.$emit('carousel-prev', this.currentPage);
      }
      this.buttonHandler();
    },
    buttonHandler() {
      this.leftControl = this.currentPage > 1;
      this.rightControl = this.currentPage !== this.numberOfPages;
      if (this.lazyload && this.itemList.length < this.maxItemsLazy) {
        this.rightControl = true;
      } else {
        this.rightControl = this.currentPage !== this.numberOfPages;
      }
    },
    next(v) {
      if (this.currentPage < this.numberOfPages) {
        this.currentPage = Math.floor(this.currentPage);
        if (typeof v === 'number') {
          if (v < this.numberOfPages - this.currentPage) {
            this.currentPage += v;
          } else {
            this.currentPage = this.numberOfPages;
          }
        } else {
          this.currentPage += 1;
        }
        this.buttonHandler();
        this.scrollerX = this.scrollerPosition();
        this.$nextTick(() => {
          this.setFocusOnItem();
        });
        if (this.lazyload) {
          this.$emit(
            'carousel-next',
            this.currentPage,
            this.numberOfPages,
            false,
          );
          this.skipScrollEnd = true;
          this.updateNativeScroll(this.scrollerX, 'smooth');
          return;
        }
        this.$emit('carousel-next', this.currentPage);
        this.skipScrollEnd = true;
        this.updateNativeScroll(this.scrollerX, 'smooth');
      }
    },
    prev(v) {
      if (this.currentPage > 1) {
        this.currentPage = Math.ceil(this.currentPage);
        if (typeof v === 'number') {
          if (v < this.currentPage) {
            this.currentPage -= v;
          } else {
            this.currentPage = 1;
          }
        } else {
          this.currentPage -= 1;
        }
        this.buttonHandler();
        this.scrollerX = this.scrollerPosition();
        this.$nextTick(() => {
          this.setFocusOnItem();
        });
        this.$emit('carousel-prev', this.currentPage);
        this.skipScrollEnd = true;
        this.updateNativeScroll(this.scrollerX, 'smooth');
      }
    },
    setFocusOnItem() {
      setTimeout(() => {
        const firstItem = this.$refs['v-scroller'].querySelector('[tabindex="0"]');
        if (firstItem) {
          firstItem.focus({ preventScroll: true });
        }
      }, 1000); // optimized time for smooth scroll to finish
    },
    scrollerPosition() {
      const itemsPerFullPage = Math.floor(this.itemsPerPage);
      if (this.currentPage >= this.numberOfPages) {
        // Last page
        return this.wrapWidth - this.scrollerWidth;
      }

      return -(
        (this.childWidth * itemsPerFullPage
          + 2 * this.marginSize() * itemsPerFullPage)
        * (this.currentPage - 1)
      );
    },
    slotsItems() {
      return this.$refs['carousel-slide'];
    },
    reset() {
      this.scrollerWidth = null;
      this.slotsItems().forEach((item) => {
        item.style.width = null; // Is there another way to not modify DOM directly?
      });
    },
    hasSlot() {
      return !!this.$slots.item && this.slotsItems().length > 0;
    },
    doSizing() {
      if (this.hasSlot()) {
        this.itemsPerPage = this.numberOfItems[this.windowSize()];
        const hasPeakItem = Number.isInteger(this.itemsPerPage) ? 1 : 0;
        this.wrapWidth = this.$refs['v-carousel-wrapper'].getBoundingClientRect().width;
        this.childWidth = (this.wrapWidth
            - 2
              * this.marginSize()
              * (Math.floor(this.numberOfItems[this.windowSize()])
                - hasPeakItem))
          / this.numberOfItems[this.windowSize()];
        this.numberOfPages = Math.ceil(
          this.slotsItems().length / Math.floor(this.itemsPerPage),
        );
        this.itemsLength = this.slotsItems().length;
        const marginSize = this.marginSize();
        this.slotsItems().forEach((item) => {
          item.style.width = `${this.childWidth}px`;
          item.style.marginLeft = `${marginSize}px`;
          item.style.marginRight = `${marginSize}px`;
        });
        const carouselUl = this.$refs['v-scroller'].querySelector('ul');
        if (carouselUl) {
          carouselUl.style.marginLeft = `-${marginSize}px`;
          carouselUl.style.marginRight = `-${marginSize}px`;
        }
        this.scrollerWidth = this.itemsLength * this.childWidth
          + 2 * this.marginSize() * (this.itemsLength - 1);
        if (this.currentPage > this.numberOfPages) {
          // this could happened on resize when number of pages are different.
          this.currentPage = this.numberOfPages;
        }
        if (this.lazyload) {
          this.scrollerX = this.slotsItems().length <= this.itemsPerPage
            ? 0
            : this.scrollerPosition();
        }
        this.buttonHandler();
      } else {
        this.$logger.warn('No items provided.');
      }
    },
    marginSize() {
      const gutter = this.gutter[this.windowSize()];
      return gutter / 2;
    },
    windowSize() {
      if (window.innerWidth < 600) return 'small';
      if (window.innerWidth >= 600 && window.innerWidth < 1024) {
        return 'medium';
      }
      return 'large';
    },
    redraw() {
      this.reset();
      this.$nextTick(() => {
        this.doSizing();
      });
    },
    eventHandler() {
      this.ro = new ResizeObserver(this.redraw);
      this.ro.observe(this.$refs['v-carousel']);
    },
    updateTabIndex() {
      this.slotsItems().forEach((item, index) => {
        const itemsPerFullPage = Math.floor(this.itemsPerPage);
        const startIndex = (this.currentPage - 1) * itemsPerFullPage;
        const endIndex = startIndex + itemsPerFullPage;
        if (index >= startIndex && index < endIndex) {
          const focusableElements = Array.from(
            item.querySelectorAll(
              'a[href]:not([disabled]), button:not([disabled]), textarea:not([disabled]), input[type="text"]:not([disabled]), input[type="radio"]:not([disabled]), input[type="checkbox"]:not([disabled]), select:not([disabled])',
            ),
          ).filter(
            (element) => element.getBoundingClientRect().width > 0
              && element.getBoundingClientRect().height > 0,
          );
          if (focusableElements.length > 0) {
            focusableElements.forEach((el) => {
              el.tabIndex = 0;
            });
            item.tabIndex = -1;
          } else {
            item.tabIndex = -1;
          }
        } else {
          // all focusable elements are -1
          const allFocusableElements = Array.from(
            item.querySelectorAll(
              'a[href]:not([disabled]), button:not([disabled]), textarea:not([disabled]), input[type="text"]:not([disabled]), input[type="radio"]:not([disabled]), input[type="checkbox"]:not([disabled]), select:not([disabled])',
            ),
          );
          allFocusableElements.forEach((el) => {
            el.tabIndex = -1;
          });
          item.tabIndex = -1;
        }
      });
    },
    manageArrowTabIndex(direction) {
      if (direction === 'prev' && this.leftControl) return 0;
      if (direction === 'next' && this.rightControl) return 0;
      return -1;
    },
    handleKeydown(event) {
      if (event.key === 'Tab') {
        const carouselEl = this.$refs['v-carousel'];
        const { activeElement } = document;
        this.keyboardUser = carouselEl && carouselEl.contains(activeElement);
      }
    },
    showArrowControl(direction) {
      const isControlVisible = direction === 'prev' ? this.leftControl : this.rightControl;
      return (
        isControlVisible
        && (!this.isTouchDevice || this.keyboardUser || this.isLargeTouchDevice)
      );
    },
  },
};
</script>

<style lang="scss" scoped>
$transition: transform 250ms cubic-bezier(0.1, 0.57, 0.1, 1);

.v-button-wrapper {
  position: absolute;
  z-index: 2;
  width: rem-calc(32);
  height: rem-calc(32);
  top: 0;
  bottom: 0;
  margin: auto;
  background-color: transparent;
  transition: transform 600ms;
  transform: translateX(-200%);
  &.prev {
    left: rem-calc(8);
  }
  &.next {
    right: rem-calc(8);
    transform: translateX(200%);
  }
}

.v-carousel-wrapper {
  overflow: auto;
  -webkit-scrollbar: none;
  -moz-scrollbar: none;
  -ms-overflow-style: none;
  -o-scrollbar: none;
  scrollbar-width: none;
  &::-webkit-scrollbar {
    -webkit-appearance: none; //safari styling
  }
  .v-scroller {
    -webkit-transition: $transition;
    -moz-transition: $transition;
    -ms-transition: $transition;
    -o-transition: $transition;
    transition: $transition;
  }
  .v-carousel-container {
    flex-wrap: nowrap;
  }
}

.v-carousel {
  margin: 0;
  touch-action: pan-y;
  .v-carousel-content {
    position: relative;
    overflow: hidden;
  }
  :deep(li) {
    display: inline-block;
  }
  .hide-carousel-item {
    visibility: hidden;
  }
}

.v-carousel-button {
  opacity: 0.65;
  border-radius: 50%;
  border: 0;
  padding: 0;
  width: 100%;
  cursor: pointer;
  height: 100%;
  min-height: 0;
  mix-blend-mode: normal;
  justify-content: center;
  align-items: center;
  background-color: $white;
  .icon {
    display: block;
    margin: 0 auto;
  }
}

.v-carousel .v-button-wrapper {
  transform: translateX(0);
  &.next {
    transform: translateX(0);
  }
}
.v-carousel .v-carousel-button {
  display: flex;
}

.hide-arrows {
  display: none;
}
</style>
<style lang="scss" brand="mcom" scoped>
$grid-margin-gutters: rem-calc(8);
@import "~@core/foundation/scss/foundation-6.7.4/_vendor/sassy-lists/stylesheets/helpers/true";
@import "~@core/foundation/scss/foundation-6.7.4/_vendor/sassy-lists/stylesheets/functions/purge";
@import "~@core/foundation/scss/foundation-6.7.4/_vendor/sassy-lists/stylesheets/functions/remove";
@import "~@core/foundation/scss/foundation-6.7.4/_vendor/sassy-lists/stylesheets/functions/replace";
@import "~@core/foundation/scss/foundation-6.7.4/_vendor/sassy-lists/stylesheets/functions/to-list";
@import "~@core/foundation/scss/foundation-6.7.4/scss/xy-grid/_gutters.scss";
@import "~@core/foundation/scss/foundation-6.7.4/scss/xy-grid/_cell.scss";
@import "~@core/foundation/scss/foundation-6.7.4/scss/xy-grid/_classes.scss";
@import "~@core/csg/src/scss/tiles/tiles.scss";
@import "~@core/csg/src/scss/forms/forms.scss";
@import "~@csgIcons/_chevron-medium-left-black.scss";
@import "~@csgIcons/_chevron-medium-right-black.scss";

.grid-margin-x {
  > .cell {
    @include breakpoint(small down) {
      margin: rem-calc(0 4);
      @include xy-cell(
        $output: (
          size gutters,
        )
      );
    }
  }
  @include breakpoint(small down) {
    margin: rem-calc(0 -4);
    @include xy-gutters($negative: true, $gutter-position: right left);
  }
}
.chevron-left-arrow {
  @extend .chevron-medium-left-black;
}
.chevron-right-arrow {
  @extend .chevron-medium-right-black;
}
</style>
<style lang="scss" brand="bcom" scoped>
$grid-margin-gutters: rem-calc(8);
@import "~@core/foundation/scss/foundation-6.7.4/_vendor/sassy-lists/stylesheets/helpers/true";
@import "~@core/foundation/scss/foundation-6.7.4/_vendor/sassy-lists/stylesheets/functions/purge";
@import "~@core/foundation/scss/foundation-6.7.4/_vendor/sassy-lists/stylesheets/functions/remove";
@import "~@core/foundation/scss/foundation-6.7.4/_vendor/sassy-lists/stylesheets/functions/replace";
@import "~@core/foundation/scss/foundation-6.7.4/_vendor/sassy-lists/stylesheets/functions/to-list";
@import "~@core/foundation/scss/foundation-6.7.4/scss/xy-grid/_gutters.scss";
@import "~@core/foundation/scss/foundation-6.7.4/scss/xy-grid/_cell.scss";
@import "~@core/foundation/scss/foundation-6.7.4/scss/xy-grid/_classes.scss";
@import "~@core/csg/src/scss/tiles/tiles.scss";
@import "~@core/csg/src/scss/forms/forms.scss";
@import "~@csgIcons/_chevron-small-left.scss";
@import "~@csgIcons/_chevron-small-right.scss";

.grid-margin-x {
  > .cell {
    @include breakpoint(medium down) {
      margin: rem-calc(0 4);
      @include xy-cell(
        $output: (
          size gutters,
        )
      );
    }
  }
  @include breakpoint(medium down) {
    margin: rem-calc(0 -4);
    @include xy-gutters($negative: true, $gutter-position: right left);
  }
}
.chevron-left-arrow {
  @extend .chevron-small-left;
}
.chevron-right-arrow {
  @extend .chevron-small-right;
}
</style>
